import React from "react";
import { Link } from "react-router-dom";
import { Coins, ShieldCheck, Truck, Sparkles, ArrowRight } from "lucide-react";
import LeadFormSticky from "@/components/site/LeadFormSticky";
import CalendlyButton from "@/components/site/CalendlyButton";
import { trackEvent } from "@/lib/analytics";

const GOLD_IMG = "https://images.pexels.com/photos/321452/pexels-photo-321452.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function GoldPage() {
  return (
    <div data-testid="gold-page">
      <LeadFormSticky source="gold" />
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <img src={GOLD_IMG} alt="Gold investment" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/75 via-[#030712]/85 to-[#030712]" />
        <div className="mfb-container relative">
          <div className="overline mb-4 text-[#D4AF37]">Gold Investment</div>
          <h1 className="font-[Outfit] text-5xl sm:text-6xl font-light tracking-tighter text-white max-w-4xl leading-[1.05]">
            <span className="gradient-text-gold">24K certified gold</span><br />from $10. Anywhere.
          </h1>
          <p className="mt-6 max-w-2xl text-slate-300 text-base leading-relaxed">
            Digital Gold, Sovereign Gold Bonds and Physical Delivery — own the world&rsquo;s most timeless asset with the world&rsquo;s most modern platform.
          </p>
          <div className="mt-8 flex gap-3">
            <Link
              to="/contact"
              className="btn-gold"
              data-testid="gold-cta-open"
              onClick={() => trackEvent("open_account_click", { source: "gold" })}
            >
              Open Gold Account <ArrowRight className="w-4 h-4" />
            </Link>
            <CalendlyButton label="Talk to Specialist" source="gold" className="btn-ghost" testId="gold-cta-advisor" />
          </div>
        </div>
      </section>

      <section className="mfb-section">
        <div className="mfb-container grid lg:grid-cols-3 gap-5">
          {[
            { i: Coins, t: "Digital Gold", d: "24K certified gold backed by physical reserves. Buy from $10, redeem any time.", h: "From $10" },
            { i: ShieldCheck, t: "Sovereign Gold Bonds", d: "Government-backed gold bonds with 2.5% additional annual interest.", h: "+2.5% p.a." },
            { i: Truck, t: "Physical Delivery", d: "Take delivery of certified bullion at insured doorstep, anywhere globally.", h: "Door delivery" },
          ].map((x, i) => (
            <div key={i} className="card-luxury p-8 relative overflow-hidden" data-testid={`gold-product-${i}`}>
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#D4AF37]/10 blur-3xl" />
              <x.i className="w-6 h-6 text-[#D4AF37]" />
              <div className="mt-4 text-white font-[Outfit] text-2xl">{x.t}</div>
              <div className="mt-2 text-slate-400 text-sm leading-relaxed">{x.d}</div>
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-1 text-xs text-[#D4AF37]">
                {x.h}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mfb-section border-t border-white/5">
        <div className="mfb-container grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline mb-3 text-[#D4AF37]">Why Gold, Why Now</div>
            <h2 className="font-[Outfit] text-4xl font-light text-white">The asset that protects, regardless of regime.</h2>
            <p className="mt-5 text-slate-400 text-sm leading-relaxed">
              Central banks accumulated a record amount of gold last year. Inflation, geopolitical tension and currency debasement make gold an essential hedge in every modern portfolio.
            </p>
            <ul className="mt-6 space-y-3 text-slate-300 text-sm">
              {[
                "12% average annualized return over 5 years",
                "Insured vault storage, audited monthly",
                "Convert digital to physical, any time",
                "Tax-efficient SGB option with 2.5% interest",
              ].map((x) => (
                <li key={x} className="flex items-center gap-3"><Sparkles className="w-4 h-4 text-[#D4AF37]" /> {x}</li>
              ))}
            </ul>
          </div>
          <div className="relative aspect-square rounded-3xl overflow-hidden border border-white/10">
            <img src={GOLD_IMG} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#030712]/60" />
            <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Spot Gold (XAU/USD)</div>
              <div className="font-[Outfit] text-3xl text-white font-light mt-1">$2,378.40</div>
              <div className="text-emerald-400 text-xs mt-0.5">+1.04% today</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
